import type { ReactNode } from "react";

// =============================================================================
// MARKDOWN UTILITY (lightweight, for blog prose)
// =============================================================================
// Standalone from the case study parser (which uses `_text_` for shimmer).
// Supports: **bold**, *italic*, [text](url), \n\n paragraphs, > blockquote, - lists
// Blog-only extras: --- (divider), >>> text (pull quote)

// ---------------------------------------------------------------------------
// Inline parsers -- run sequentially on a single line of text
// ---------------------------------------------------------------------------

function parseLinks(text: string, keyPrefix: string): ReactNode[] {
  const parts: ReactNode[] = [];
  const regex = /\[([^\]]+)\]\(([^)]+)\)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let i = 0;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    const [, linkText, url] = match;
    const isExternal = /^https?:\/\//.test(url);
    parts.push(
      <a
        key={`${keyPrefix}-link-${i++}`}
        href={url}
        target={isExternal ? "_blank" : undefined}
        rel={isExternal ? "noreferrer" : undefined}
        className="underline decoration-[#A78BFA]/50 hover:decoration-[#A78BFA] transition-colors"
      >
        {linkText}
      </a>
    );
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts;
}

function parseBold(nodes: ReactNode[], keyPrefix: string): ReactNode[] {
  const out: ReactNode[] = [];
  let i = 0;
  for (const node of nodes) {
    if (typeof node !== "string") {
      out.push(node);
      continue;
    }
    const regex = /\*\*([^*]+)\*\*/g;
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = regex.exec(node)) !== null) {
      if (match.index > lastIndex) {
        out.push(node.slice(lastIndex, match.index));
      }
      out.push(
        <strong key={`${keyPrefix}-bold-${i++}`} className="font-semibold text-[rgba(var(--foreground-rgb),1)]">
          {match[1]}
        </strong>
      );
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < node.length) {
      out.push(node.slice(lastIndex));
    }
  }
  return out;
}

function parseItalic(nodes: ReactNode[], keyPrefix: string): ReactNode[] {
  const out: ReactNode[] = [];
  let i = 0;
  for (const node of nodes) {
    if (typeof node !== "string") {
      out.push(node);
      continue;
    }
    // Match single * not preceded or followed by another * (to avoid **)
    const regex = /(?<!\*)\*(?!\*)([^*]+)(?<!\*)\*(?!\*)/g;
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = regex.exec(node)) !== null) {
      if (match.index > lastIndex) {
        out.push(node.slice(lastIndex, match.index));
      }
      out.push(
        <em key={`${keyPrefix}-italic-${i++}`} className="italic">
          {match[1]}
        </em>
      );
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < node.length) {
      out.push(node.slice(lastIndex));
    }
  }
  return out;
}

/**
 * Parse a single line of text with inline markdown.
 * Supports: **bold**, *italic*, [text](url)
 */
export function parseInline(text: string, keyPrefix = "inline"): ReactNode {
  let nodes: ReactNode[] = parseLinks(text, keyPrefix);
  nodes = parseBold(nodes, keyPrefix);
  nodes = parseItalic(nodes, keyPrefix);
  return <>{nodes}</>;
}

// ---------------------------------------------------------------------------
// Block-level renderer -- splits text into blocks by blank lines
// ---------------------------------------------------------------------------

type Block =
  | { kind: "paragraph"; text: string }
  | { kind: "blockquote"; text: string }
  | { kind: "list"; items: string[] }
  | { kind: "divider"; label?: string }
  | { kind: "pullquote"; text: string }
  | { kind: "image"; src: string; alt: string };

function tokenizeBlocks(text: string): Block[] {
  const blocks: Block[] = [];
  // Normalize line endings, split on blank lines
  const paragraphs = text.replace(/\r\n/g, "\n").trim().split(/\n\s*\n/);

  for (const raw of paragraphs) {
    const lines = raw.split("\n");

    // Divider: `---` or `--- LABEL` for tagged dividers (e.g. `--- WIP`)
    const dividerMatch = raw.trim().match(/^---(?:\s+(.+))?$/);
    if (dividerMatch) {
      blocks.push({ kind: "divider", label: dividerMatch[1]?.trim() || undefined });
      continue;
    }

    // Image: standalone `![alt](src)` block
    const imgMatch = raw.trim().match(/^!\[([^\]]*)\]\(([^)]+)\)$/);
    if (imgMatch) {
      blocks.push({ kind: "image", alt: imgMatch[1], src: imgMatch[2] });
      continue;
    }

    // Pull quote: line(s) starting with `>>> `
    if (lines[0].trim().startsWith(">>> ")) {
      const text = lines.map((l) => l.replace(/^\s*>>>\s?/, "")).join(" ");
      blocks.push({ kind: "pullquote", text });
      continue;
    }

    // Blockquote: every line starts with `> `
    if (lines.every((l) => l.trim().startsWith(">"))) {
      const stripped = lines.map((l) => l.replace(/^\s*>\s?/, "")).join(" ");
      blocks.push({ kind: "blockquote", text: stripped });
      continue;
    }

    // List: every line starts with `- `
    if (lines.every((l) => l.trim().startsWith("-"))) {
      const items = lines.map((l) => l.replace(/^\s*-\s?/, ""));
      blocks.push({ kind: "list", items });
      continue;
    }

    // Default: paragraph (collapse internal line breaks to spaces)
    blocks.push({ kind: "paragraph", text: lines.join(" ") });
  }

  return blocks;
}

interface RenderProseOptions {
  dropCap?: boolean;
}

/**
 * Render multi-paragraph prose with markdown support.
 * Used for blog post bodies.
 */
export function renderProse(text: string, keyPrefix = "prose", options?: RenderProseOptions): ReactNode {
  const blocks = tokenizeBlocks(text);
  const firstParaIdx = options?.dropCap ? blocks.findIndex((b) => b.kind === "paragraph") : -1;

  return (
    <>
      {blocks.map((block, i) => {
        const key = `${keyPrefix}-${i}`;

        if (block.kind === "divider") {
          return (
            <div key={key} className="my-12 flex flex-col items-center gap-2" aria-hidden="true">
              <span
                style={{
                  color: "rgba(255,255,255,0.3)",
                  fontSize: "14px",
                  letterSpacing: "1.5em",
                  paddingLeft: "1.5em",
                }}
              >
                ✧ ✧ ✧
              </span>
              {block.label && (
                <span
                  style={{
                    fontSize: "10px",
                    fontFamily: "var(--font-mono, monospace)",
                    fontWeight: 700,
                    letterSpacing: "0.2em",
                    color: "#fbbf24",
                    background: "rgba(251,191,36,0.12)",
                    border: "1px solid rgba(251,191,36,0.4)",
                    padding: "2px 8px",
                    borderRadius: "3px",
                    textTransform: "uppercase",
                  }}
                >
                  {block.label}
                </span>
              )}
            </div>
          );
        }

        if (block.kind === "pullquote") {
          return (
            <div
              key={key}
              style={{ margin: "2.5rem 0", padding: "0 1.5rem", textAlign: "center" }}
            >
              <p
                style={{
                  fontSize: "24px",
                  fontWeight: 500,
                  color: "white",
                  lineHeight: 1.4,
                  letterSpacing: "0.01em",
                }}
              >
                {parseInline(block.text, key)}
              </p>
            </div>
          );
        }

        if (block.kind === "image") {
          const isPlaceholder = block.src === "placeholder" || block.src.startsWith("placeholder:");
          return (
            <div key={key} style={{ margin: "2rem auto", maxWidth: "520px" }}>
              {isPlaceholder ? (
                <div
                  style={{
                    border: "1.5px dashed rgba(167,139,250,0.35)",
                    borderRadius: "10px",
                    padding: "2.5rem 1.5rem",
                    textAlign: "center",
                    color: "rgba(167,139,250,0.6)",
                    fontSize: "13px",
                    letterSpacing: "0.04em",
                    background: "rgba(167,139,250,0.04)",
                  }}
                >
                  <div style={{ fontSize: "22px", marginBottom: "8px" }}>📷</div>
                  <div>{block.alt}</div>
                </div>
              ) : (
                <img
                  src={block.src}
                  alt={block.alt}
                  style={{ width: "100%", borderRadius: "10px", display: "block" }}
                />
              )}
            </div>
          );
        }

        if (block.kind === "paragraph") {
          if (i === firstParaIdx && block.text.length > 0) {
            const firstChar = block.text[0];
            const rest = block.text.slice(1);
            return (
              <p key={key} className="mb-4 last:mb-0">
                <span
                  style={{
                    display: "inline-block",
                    fontSize: "56px",
                    fontWeight: 700,
                    color: "#a78bfa",
                    lineHeight: 0.6,
                    verticalAlign: "baseline",
                    paddingRight: "6px",
                    transform: "translateY(2px)",
                  }}
                >
                  {firstChar}
                </span>
                {parseInline(rest, key)}
              </p>
            );
          }
          return (
            <p key={key} className="mb-4 last:mb-0">
              {parseInline(block.text, key)}
            </p>
          );
        }

        if (block.kind === "blockquote") {
          return (
            <blockquote
              key={key}
              className="mb-4 last:mb-0 border-l-2 border-[#A78BFA]/40 pl-4 italic text-[rgba(var(--foreground-rgb),0.7)]"
            >
              {parseInline(block.text, key)}
            </blockquote>
          );
        }

        return (
          <ul key={key} className="mb-4 last:mb-0 list-disc pl-5 space-y-1">
            {block.items.map((item, j) => (
              <li key={`${key}-${j}`}>{parseInline(item, `${key}-${j}`)}</li>
            ))}
          </ul>
        );
      })}
    </>
  );
}
